import { useState } from 'react';
import { erpApi } from '../../../api';
import { Dialog, Radio } from '../../primitives';
import type { LinkedWebsite } from '../labels';
import { formatNumber, websiteLabel } from '../labels';
import { defaultDelimiter } from './matchingCopy';

/**
 * Downloads one website's products (CO-170): their real SKUs and names in
 * code_ecom and name_ecom. The person chooses the scope: the products that
 * still need a match (no ERP code, or a code the ERP does not have), with
 * code_erp and name_erp left blank to fill in, or every product, where a
 * matched one also has its ERP code and the article's name. Either file is
 * exactly what the import reads, and the scope is chosen before the download.
 */
export function ExportMatchesDialog({
  open,
  connectionId,
  target,
  withStore,
  needMatch,
  total,
  onClose,
}: {
  open: boolean;
  connectionId: string;
  target: LinkedWebsite;
  withStore: boolean;
  /** How many products need a match, when the list knows. */
  needMatch: number | undefined;
  /** How many products the website has, when the list knows. */
  total: number | undefined;
  onClose: () => void;
}) {
  const [scope, setScope] = useState<'need_match' | 'all'>('need_match');
  const [format, setFormat] = useState<'xlsx' | 'csv'>('xlsx');
  const [delimiter, setDelimiter] = useState<'comma' | 'semicolon'>(defaultDelimiter);
  const website = websiteLabel(target.store, target.website, withStore);
  const href = erpApi.matchExportUrl(connectionId, {
    installation_id: target.store.id,
    website_code: target.website.code,
    ...(scope === 'all' ? { scope } : {}),
    format,
    ...(format === 'csv' ? { delimiter } : {}),
  });
  return (
    <Dialog
      open={open}
      size="md"
      title="Export products"
      onClose={onClose}
      testId="export-matches-dialog"
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose}>
            Cancel
          </button>
          <a className="button" href={href} download onClick={() => window.setTimeout(onClose, 0)} data-testid="export-download">
            Download the file
          </a>
        </>
      }
    >
      <dl className="match-export__scope" aria-label="What the file has">
        <div>
          <dt>Website</dt>
          <dd>{website}</dd>
        </div>
      </dl>
      <fieldset className="field match-delimiter">
        <legend>Products</legend>
        <Radio
          id="match-scope-need"
          name="match-scope"
          checked={scope === 'need_match'}
          onChange={() => setScope('need_match')}
          label={`${needMatch === undefined ? 'Those' : formatNumber(needMatch)} that need a match: no ERP code, or a code the ERP does not have`}
        />
        <Radio
          id="match-scope-all"
          name="match-scope"
          checked={scope === 'all'}
          onChange={() => setScope('all')}
          label={`${total === undefined ? 'All' : `All ${formatNumber(total)}`} products, matched ones with their ERP code`}
        />
      </fieldset>
      <p data-testid="export-scope">
        {scope === 'all' ? (
          <>
            <code>code_erp</code> and <code>name_erp</code> are filled in for matched products and blank for the rest; only <code>code_ecom</code> and <code>code_erp</code> decide anything when the file is imported.
          </>
        ) : (
          <>
            <code>code_erp</code> and <code>name_erp</code> are blank for you to fill in; only <code>code_ecom</code> and <code>code_erp</code> decide anything when the file is imported.
          </>
        )}
      </p>
      <fieldset className="field match-delimiter">
        <legend>File type</legend>
        <Radio id="match-format-xlsx" name="match-format" checked={format === 'xlsx'} onChange={() => setFormat('xlsx')} label="Excel (.xlsx), recommended: codes stay text, so leading zeros stay, and a dropdown offers the ERP’s articles (code, name, stock)" />
        <Radio id="match-format-csv" name="match-format" checked={format === 'csv'} onChange={() => setFormat('csv')} label="CSV: Excel drops leading zeros when it opens one, so format the code columns as Text first" />
      </fieldset>
      {format === 'csv' ? (
        <fieldset className="field match-delimiter">
          <legend>Separate the columns with</legend>
          <Radio id="match-delimiter-comma" name="match-delimiter" checked={delimiter === 'comma'} onChange={() => setDelimiter('comma')} label="Commas (Google Sheets and most programs)" />
          <Radio id="match-delimiter-semicolon" name="match-delimiter" checked={delimiter === 'semicolon'} onChange={() => setDelimiter('semicolon')} label="Semicolons (Excel in Romania and many other countries)" />
        </fieldset>
      ) : null}
    </Dialog>
  );
}
